import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import KoobTitle from './KoobTitle';

const layoutVariants = cva('w-full mx-auto flex flex-col', {
  variants: {
    // narrow = small CRUD/settings tables, wide = standard admin lists,
    // full = dense data pages (bookings, folders)
    width: {
      full: '',
      wide: 'max-w-7xl',
      narrow: 'max-w-3xl',
    },
  },
  defaultVariants: {
    width: 'full',
  },
});

type KoobListPageLayoutProps = {
  /** Page title; a string renders as KoobTitle, a node renders as-is. */
  title?: React.ReactNode;
  /** Results count / description line under the title. */
  subtitle?: React.ReactNode;
  /** Header-right cluster: create button, view toggle, … */
  actions?: React.ReactNode;
  /** Search input; rendered at the standard fixed width in the toolbar row. */
  search?: React.ReactNode;
  /** Inline filter controls, rendered next to the search input. */
  filters?: React.ReactNode;
  /** Right-aligned toolbar extras: export, bulk edit, locale switcher, … */
  toolbarActions?: React.ReactNode;
  children: React.ReactNode;
} & VariantProps<typeof layoutVariants>;

// The one list-page scaffold: header row (title + count + actions), toolbar
// row (fixed-width search + filters + right-aligned extras), then the table.
// Every slot is optional so DMC pages keeping their sticky Header can use
// only the toolbar/width parts.
const KoobListPageLayout = React.forwardRef<
  HTMLDivElement,
  KoobListPageLayoutProps
>(
  (
    {
      title,
      subtitle,
      actions,
      search,
      filters,
      toolbarActions,
      width,
      children,
    },
    ref,
  ) => {
    const hasHeader = Boolean(title || subtitle || actions);
    const hasToolbar = Boolean(search || filters || toolbarActions);

    return (
      <div ref={ref} className={layoutVariants({ width })}>
        {hasHeader && (
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex flex-col gap-1">
              {typeof title === 'string' ? (
                <KoobTitle>{title}</KoobTitle>
              ) : (
                title
              )}
              {Boolean(subtitle) && (
                <p className="text-sm text-k-p">{subtitle}</p>
              )}
            </div>
            {Boolean(actions) && (
              <div className="flex flex-wrap items-center gap-3">{actions}</div>
            )}
          </div>
        )}
        {hasToolbar && (
          <div className="flex flex-col lg:flex-row lg:items-end gap-4 mb-6">
            {Boolean(search) && <div className="w-full lg:w-96">{search}</div>}
            {Boolean(filters) && (
              <div className="flex flex-wrap items-end gap-3">{filters}</div>
            )}
            {Boolean(toolbarActions) && (
              <div className="flex flex-wrap items-center gap-3 lg:ml-auto">
                {toolbarActions}
              </div>
            )}
          </div>
        )}
        {children}
      </div>
    );
  },
);

KoobListPageLayout.displayName = 'KoobListPageLayout';

export default KoobListPageLayout;
